// src/components/workspace/costing/PriceToWinAnalysis.tsx
// AI-powered Price-to-Win Analysis

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Target, ArrowLeft, Loader2, Sparkles, TrendingUp, TrendingDown,
  DollarSign, BarChart3, AlertCircle, CheckCircle, Info, ExternalLink,
  Building2, Calendar, Award, Percent, ArrowRight, RefreshCw, FileText,
  Search, ChevronRight
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../lib/pricing/utils';
import toast from 'react-hot-toast';

interface ComparableAward {
  contractNumber: string;
  title: string;
  agency: string;
  awardDate: string;
  value: number;
  contractor: string;
  naicsCode: string;
}

interface PriceAnalysis {
  suggestedPrice: number;
  confidenceLevel: 'HIGH' | 'MEDIUM' | 'LOW';
  priceRange: {
    low: number;
    target: number;
    high: number;
  };
  competitivePosition: 'AGGRESSIVE' | 'COMPETITIVE' | 'PREMIUM';
  factors: {
    marketAverage: number;
    incumbentEstimate: number | null;
    historicalWins: number;
    competitorEstimate: number | null;
  };
  recommendations: string[];
  risks: string[];
}

interface ContractContext {
  id: string;
  title: string;
  agency: string;
  naicsCode?: string;
  estimatedValue?: number;
  incumbent?: string;
}

interface ProposalOption {
  id: string;
  title: string;
  agency: string;
  status: string;
  created_at: string;
  notice_id?: string;
}

export function PriceToWinAnalysis() {
  const navigate = useNavigate();
  const { id: proposalId } = useParams();
  const { profile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [contract, setContract] = useState<ContractContext | null>(null);
  const [analysis, setAnalysis] = useState<PriceAnalysis | null>(null);
  const [comparables, setComparables] = useState<ComparableAward[]>([]);
  const [yourPrice, setYourPrice] = useState<number>(0);

  // For proposal selection when no proposalId in URL
  const [proposals, setProposals] = useState<ProposalOption[]>([]);
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(proposalId || null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (profile) {
      if (proposalId) {
        // Direct proposal ID from URL
        setSelectedProposalId(proposalId);
        loadContractData(proposalId);
      } else {
        // No proposal ID - load proposals for selection
        loadProposals();
      }
    }
  }, [profile, proposalId]);

  const loadProposals = async () => {
    try {
      setLoading(true);

      // Guard check for profile
      if (!profile?.id) {
        console.error('No profile ID available');
        setLoading(false);
        return;
      }

      console.log('Loading proposals for profile:', profile.id);

      // Load proposal templates - matching ProposalWorkspace pattern
      const { data: proposalData, error: proposalError } = await supabase
        .from('proposal_templates')
        .select('*')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (proposalError) {
        console.error('Error loading proposals:', proposalError);
        toast.error('Failed to load proposals');
        setLoading(false);
        return;
      }

      console.log('Loaded proposal templates:', proposalData?.length || 0);

      // Get unique notice_ids to fetch contract details
      const noticeIds = [...new Set((proposalData || []).map(p => p.notice_id).filter(Boolean))];
      console.log('Notice IDs to fetch:', noticeIds);

      // Fetch contract details separately for each notice_id
      let contractsMap: Record<string, any> = {};
      if (noticeIds.length > 0) {
        const { data: contractsData, error: contractsError } = await supabase
          .from('sam_contracts')
          .select('*')
          .in('notice_id', noticeIds);

        if (contractsError) {
          console.error('Error loading contracts:', contractsError);
        }

        if (contractsData) {
          contractsMap = contractsData.reduce((acc: Record<string, any>, c: any) => {
            acc[c.notice_id] = c;
            return acc;
          }, {});
          console.log('Loaded contracts:', Object.keys(contractsMap).length);
        }
      }

      const formattedProposals: ProposalOption[] = (proposalData || []).map((p: any) => {
        const contract = p.notice_id ? contractsMap[p.notice_id] : null;
        return {
          id: p.id,
          title: contract?.title || p.template_name || 'Untitled Proposal',
          agency: contract?.agency || 'Unknown Agency',
          status: p.status || 'draft',
          created_at: p.created_at,
          notice_id: p.notice_id
        };
      });

      console.log('Formatted proposals:', formattedProposals.length);
      setProposals(formattedProposals);
    } catch (error) {
      console.error('Error loading proposals:', error);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  const loadContractData = async (propId: string) => {
    try {
      setLoading(true);

      // Guard check for profile
      if (!profile?.id) {
        console.error('No profile ID available');
        setLoading(false);
        return;
      }

      console.log('Loading contract data for proposal:', propId);

      // Load proposal template - matching ProposalWorkspace pattern with profile_id check
      const { data: proposal, error: proposalError } = await supabase
        .from('proposal_templates')
        .select('*')
        .eq('id', propId)
        .eq('profile_id', profile.id)
        .single();

      if (proposalError) {
        console.error('Error loading proposal:', proposalError);
        // Still allow analysis with manual entry
        setContract({
          id: propId,
          title: 'Manual Analysis',
          agency: 'Enter contract details below'
        });
        setLoading(false);
        return;
      }

      console.log('Proposal loaded:', { id: proposal.id, notice_id: proposal.notice_id, template_name: proposal.template_name });

      // If we have a notice_id, fetch the contract details - matching ProposalWorkspace pattern
      let contractData: any = null;
      if (proposal?.notice_id) {
        // First try by notice_id
        let { data: samContract } = await supabase
          .from('sam_contracts')
          .select('*')
          .eq('notice_id', proposal.notice_id)
          .maybeSingle();

        // If not found, try by id (for legacy data)
        if (!samContract) {
          const result = await supabase
            .from('sam_contracts')
            .select('*')
            .eq('id', proposal.notice_id)
            .maybeSingle();
          samContract = result.data;
        }

        contractData = samContract;
        console.log('Contract data loaded:', contractData ? 'found' : 'not found');
      }

      if (contractData) {
        setContract({
          id: contractData.id,
          title: contractData.title || proposal?.template_name || 'Untitled Contract',
          agency: contractData.agency || 'Unknown Agency',
          naicsCode: contractData.naics_code,
          estimatedValue: contractData.award_amount || undefined,
          incumbent: undefined
        });
      } else {
        // Try to extract contract info from content_json
        const content = proposal?.content_json;
        setContract({
          id: propId,
          title: content?.contractOverview?.title || proposal?.template_name || 'Custom Analysis',
          agency: content?.contractOverview?.agency || 'Enter details below',
          naicsCode: content?.contractOverview?.naicsCode,
          estimatedValue: content?.pricingStrategy?.costBreakdown?.totalContractValue
            ? parseFloat(content.pricingStrategy.costBreakdown.totalContractValue.toString().replace(/[^0-9.]/g, ''))
            : undefined
        });
      }

      // Set initial price from proposal pricing data if available
      const content = proposal?.content_json;
      if (content?.pricingStrategy?.costBreakdown?.totalContractValue) {
        const totalValue = parseFloat(
          content.pricingStrategy.costBreakdown.totalContractValue.toString().replace(/[^0-9.]/g, '')
        );
        if (!isNaN(totalValue) && totalValue > 0) {
          setYourPrice(totalValue);
        }
      }

    } catch (error) {
      console.error('Error loading contract data:', error);
      setContract({
        id: propId,
        title: 'Manual Analysis',
        agency: 'Enter contract details below'
      });
    } finally {
      setLoading(false);
    }
  };

  const selectProposal = (propId: string) => {
    setSelectedProposalId(propId);
    setAnalysis(null);
    setComparables([]);
    loadContractData(propId);
  };

  const runAnalysis = async () => {
    setAnalyzing(true);

    try {
      // In production, this would call an AI endpoint with:
      // - Historical USASpending.gov data
      // - FPDS contract data
      // - Market intelligence
      // For now, simulate the analysis

      await new Promise(resolve => setTimeout(resolve, 2000));

      // Use estimated value or a default
      const baseValue = contract?.estimatedValue || yourPrice || 1500000;

      // Generate mock comparable awards
      const mockComparables: ComparableAward[] = [
        {
          contractNumber: 'GS-00F-123AA',
          title: 'IT Support Services',
          agency: contract?.agency || 'General Services Administration',
          awardDate: '2024-08-15',
          value: baseValue * 0.95,
          contractor: 'Tech Solutions Inc.',
          naicsCode: contract?.naicsCode || '541512'
        },
        {
          contractNumber: 'HHSN316201800001W',
          title: 'Enterprise IT Services',
          agency: contract?.agency || 'Department of Health and Human Services',
          awardDate: '2024-03-22',
          value: baseValue * 1.05,
          contractor: 'Federal IT Corp',
          naicsCode: contract?.naicsCode || '541512'
        },
        {
          contractNumber: 'VA118-21-D-1001',
          title: 'Digital Modernization',
          agency: 'Department of Veterans Affairs',
          awardDate: '2023-11-30',
          value: baseValue * 0.88,
          contractor: 'Digital Gov Partners',
          naicsCode: contract?.naicsCode || '541512'
        },
        {
          contractNumber: 'DOTMA-22-C-00089',
          title: 'Cloud Migration Services',
          agency: 'Department of Transportation',
          awardDate: '2023-09-10',
          value: baseValue * 1.12,
          contractor: 'CloudFirst Federal',
          naicsCode: contract?.naicsCode || '541512'
        }
      ];

      setComparables(mockComparables);

      // Calculate market average
      const marketAverage = mockComparables.reduce((sum, c) => sum + c.value, 0) / mockComparables.length;

      // Generate analysis
      const mockAnalysis: PriceAnalysis = {
        suggestedPrice: Math.round(baseValue * 0.92),
        confidenceLevel: contract?.estimatedValue ? 'MEDIUM' : 'LOW',
        priceRange: {
          low: Math.round(baseValue * 0.85),
          target: Math.round(baseValue * 0.92),
          high: Math.round(baseValue * 1.05)
        },
        competitivePosition: 'COMPETITIVE',
        factors: {
          marketAverage: Math.round(marketAverage),
          incumbentEstimate: contract?.incumbent ? Math.round(baseValue * 1.02) : null,
          historicalWins: mockComparables.length,
          competitorEstimate: Math.round(baseValue * 0.95)
        },
        recommendations: [
          'Price 5-8% below the estimated government budget to remain competitive',
          'Consider a phased pricing approach to demonstrate cost efficiency',
          'Highlight past performance cost savings in technical volume',
          'Include detailed cost breakdown to demonstrate transparency'
        ],
        risks: [
          'Pricing too low may raise concerns about quality or understanding of scope',
          'Incumbent advantage may require additional value proposition to compete',
          'Budget constraints may lead to LPTA evaluation favoring lowest price'
        ]
      };

      setAnalysis(mockAnalysis);
      toast.success('Price-to-Win analysis complete');

    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Failed to run analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const getPositionColor = (position: string) => {
    switch (position) {
      case 'AGGRESSIVE': return 'text-urgent bg-urgent-bg';
      case 'COMPETITIVE': return 'text-success bg-success-bg';
      case 'PREMIUM': return 'text-bw-primary bg-bw-primary-tint';
      default: return 'text-bw-muted bg-bw-surface-subtle';
    }
  };

  const getConfidenceColor = (level: string) => {
    switch (level) {
      case 'HIGH': return 'text-success';
      case 'MEDIUM': return 'text-fit-moderate';
      case 'LOW': return 'text-urgent';
      default: return 'text-bw-muted';
    }
  };

  const calculatePricePosition = () => {
    if (!analysis || !yourPrice) return null;

    const { low, target, high } = analysis.priceRange;
    const range = high - low;

    if (yourPrice < low) return { position: 'below', percent: 0 };
    if (yourPrice > high) return { position: 'above', percent: 100 };

    const percent = ((yourPrice - low) / range) * 100;
    return { position: 'within', percent };
  };

  const filteredProposals = proposals.filter(p =>
    p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.agency.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-bw-primary" />
      </div>
    );
  }

  // Show proposal selector if no proposal selected
  if (!selectedProposalId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/workspace/costing')}
            className="flex items-center text-sm text-bw-muted hover:text-bw-ink mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Costing Studio
          </button>
          <h1 className="text-2xl font-bold text-bw-ink flex items-center">
            <Target className="h-6 w-6 mr-2 text-bw-primary" />
            Price-to-Win Analysis
          </h1>
          <p className="text-bw-muted mt-1">Select a proposal to analyze competitive pricing</p>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-bw-muted" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search proposals..."
            className="w-full pl-10 pr-4 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent"
          />
        </div>

        {/* Proposals List */}
        <div className="bg-bw-surface rounded-xl border border-bw-border divide-y divide-gray-100">
          {filteredProposals.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-bw-muted">No proposals found</p>
              <p className="text-sm text-bw-muted mt-1">
                Create a proposal first, then run price-to-win analysis
              </p>
              <button
                onClick={() => navigate('/workspace')}
                className="mt-4 px-4 py-2 text-bw-primary hover:bg-bw-primary-tint rounded-lg transition-colors"
              >
                Go to Workspace
              </button>
            </div>
          ) : (
            filteredProposals.map((proposal) => (
              <button
                key={proposal.id}
                onClick={() => selectProposal(proposal.id)}
                className="w-full p-4 text-left hover:bg-bw-surface-subtle transition-colors flex items-center justify-between"
              >
                <div>
                  <h3 className="font-medium text-bw-ink">{proposal.title}</h3>
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-sm text-bw-muted">{proposal.agency}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      proposal.status === 'complete' ? 'bg-success-bg text-success' :
                      proposal.status === 'in_progress' ? 'bg-bw-primary-tint text-bw-primary' :
                      'bg-bw-surface-subtle text-bw-muted'
                    }`}>
                      {proposal.status}
                    </span>
                    <span className="text-xs text-bw-muted">
                      {new Date(proposal.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-bw-muted" />
              </button>
            ))
          )}
        </div>

        {/* Manual Analysis Option */}
        <div className="mt-6 p-4 bg-bw-primary-tint rounded-lg border border-blue-200">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-bw-primary mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-bw-primary">Quick Analysis Without a Proposal</p>
              <p className="text-sm text-bw-primary mt-1">
                You can also run a price-to-win analysis by entering contract details manually.
              </p>
              <button
                onClick={() => {
                  setSelectedProposalId('manual');
                  setContract({
                    id: 'manual',
                    title: 'Manual Analysis',
                    agency: 'Enter details below'
                  });
                }}
                className="mt-2 text-sm text-bw-primary hover:text-bw-primary font-medium"
              >
                Start Manual Analysis →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/workspace/costing')}
            className="flex items-center text-sm text-bw-muted hover:text-bw-ink mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Costing Studio
          </button>
          <h1 className="text-2xl font-bold text-bw-ink flex items-center">
            <Target className="h-6 w-6 mr-2 text-bw-primary" />
            Price-to-Win Analysis
          </h1>
          {contract && (
            <p className="text-sm text-bw-muted mt-1">{contract.title}</p>
          )}
        </div>

        <button
          onClick={runAnalysis}
          disabled={analyzing}
          className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50"
        >
          {analyzing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          {analysis ? 'Refresh Analysis' : 'Run AI Analysis'}
        </button>
      </div>

      {/* Contract Context */}
      <div className="bg-bw-surface rounded-xl border border-bw-border p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-bw-ink">Contract Details</h3>
          {!proposalId && (
            <button
              onClick={() => {
                setSelectedProposalId(null);
                setContract(null);
                setAnalysis(null);
              }}
              className="text-sm text-bw-primary hover:text-bw-primary"
            >
              Change Proposal
            </button>
          )}
        </div>
        {contract && contract.id !== 'manual' ? (
          <div className="grid grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-bw-muted">Agency</p>
              <p className="font-medium text-bw-ink">{contract.agency}</p>
            </div>
            <div>
              <p className="text-xs text-bw-muted">NAICS Code</p>
              <p className="font-medium text-bw-ink">{contract.naicsCode || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-bw-muted">Est. Value</p>
              <p className="font-medium text-bw-ink">
                {contract.estimatedValue ? formatCurrency(contract.estimatedValue) : 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-xs text-bw-muted">Incumbent</p>
              <p className="font-medium text-bw-ink">{contract.incumbent || 'Unknown'}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-bw-muted mb-1">Estimated Contract Value</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-bw-muted" />
                <input
                  type="number"
                  value={yourPrice || ''}
                  onChange={(e) => setYourPrice(parseFloat(e.target.value) || 0)}
                  placeholder="Enter estimated value"
                  className="w-full pl-8 pr-4 py-2 border border-bw-border rounded-lg"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-bw-muted mb-1">NAICS Code (Optional)</label>
              <input
                type="text"
                placeholder="e.g., 541512"
                className="w-full px-4 py-2 border border-bw-border rounded-lg"
                onChange={(e) => setContract(prev => prev ? {...prev, naicsCode: e.target.value} : null)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Analysis Results */}
      {analysis ? (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-bw-surface-subtle rounded-xl border border-green-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <Target className="h-5 w-5 text-success" />
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPositionColor(analysis.competitivePosition)}`}>
                  {analysis.competitivePosition}
                </span>
              </div>
              <p className="text-2xl font-bold text-success">{formatCurrency(analysis.suggestedPrice)}</p>
              <p className="text-xs text-success mt-1">AI Suggested Price</p>
            </div>

            <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
              <div className="flex items-center mb-2">
                <BarChart3 className="h-5 w-5 text-bw-primary mr-2" />
                <span className="text-sm font-medium text-bw-ink">Market Average</span>
              </div>
              <p className="text-2xl font-bold text-bw-ink">{formatCurrency(analysis.factors.marketAverage)}</p>
              <p className="text-xs text-bw-muted mt-1">Based on {analysis.factors.historicalWins} similar awards</p>
            </div>

            <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
              <div className="flex items-center mb-2">
                <Building2 className="h-5 w-5 text-bw-primary mr-2" />
                <span className="text-sm font-medium text-bw-ink">Incumbent Est.</span>
              </div>
              <p className="text-2xl font-bold text-bw-ink">
                {analysis.factors.incumbentEstimate
                  ? formatCurrency(analysis.factors.incumbentEstimate)
                  : 'N/A'}
              </p>
              <p className="text-xs text-bw-muted mt-1">Estimated recompete price</p>
            </div>

            <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
              <div className="flex items-center mb-2">
                <Percent className="h-5 w-5 text-fit-moderate mr-2" />
                <span className="text-sm font-medium text-bw-ink">Confidence</span>
              </div>
              <p className={`text-2xl font-bold ${getConfidenceColor(analysis.confidenceLevel)}`}>
                {analysis.confidenceLevel}
              </p>
              <p className="text-xs text-bw-muted mt-1">Based on data quality</p>
            </div>
          </div>

          {/* Price Range Visualization */}
          <div className="bg-bw-surface rounded-xl border border-bw-border p-6">
            <h3 className="font-semibold text-bw-ink mb-4">Recommended Price Range</h3>

            <div className="relative pt-8 pb-4">
              {/* Range Bar */}
              <div className="h-4 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-full relative">
                {/* Target Marker */}
                <div
                  className="absolute -top-6 transform -translate-x-1/2"
                  style={{ left: '50%' }}
                >
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-medium text-success">Target</span>
                    <div className="w-0.5 h-3 bg-success"></div>
                  </div>
                </div>

                {/* Your Price Marker */}
                {yourPrice > 0 && (() => {
                  const pos = calculatePricePosition();
                  if (!pos) return null;

                  return (
                    <div
                      className="absolute -bottom-6 transform -translate-x-1/2"
                      style={{ left: `${Math.min(Math.max(pos.percent, 5), 95)}%` }}
                    >
                      <div className="flex flex-col items-center">
                        <div className="w-0.5 h-3 bg-bw-primary"></div>
                        <span className="text-xs font-medium text-bw-primary">Your Price</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Labels */}
              <div className="flex justify-between mt-6 text-sm">
                <div className="text-center">
                  <p className="font-medium text-success">{formatCurrency(analysis.priceRange.low)}</p>
                  <p className="text-xs text-bw-muted">Aggressive</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-success">{formatCurrency(analysis.priceRange.target)}</p>
                  <p className="text-xs text-bw-muted">Target</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-fit-moderate">{formatCurrency(analysis.priceRange.high)}</p>
                  <p className="text-xs text-bw-muted">Premium</p>
                </div>
              </div>
            </div>

            {/* Your Price Input */}
            <div className="mt-6 pt-4 border-t border-bw-border">
              <label className="block text-sm font-medium text-bw-ink mb-2">Your Proposed Price</label>
              <div className="flex items-center space-x-4">
                <div className="relative flex-1 max-w-xs">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-bw-muted" />
                  <input
                    type="number"
                    value={yourPrice}
                    onChange={(e) => setYourPrice(parseFloat(e.target.value) || 0)}
                    className="w-full pl-8 pr-4 py-2 border border-bw-border rounded-lg"
                    placeholder="Enter your price"
                  />
                </div>
                {yourPrice > 0 && analysis && (
                  <div className="flex items-center">
                    {yourPrice < analysis.priceRange.target ? (
                      <TrendingDown className="h-5 w-5 text-success mr-2" />
                    ) : (
                      <TrendingUp className="h-5 w-5 text-fit-moderate mr-2" />
                    )}
                    <span className={`text-sm font-medium ${
                      yourPrice < analysis.priceRange.target ? 'text-success' : 'text-fit-moderate'
                    }`}>
                      {((yourPrice - analysis.priceRange.target) / analysis.priceRange.target * 100).toFixed(1)}%
                      {yourPrice < analysis.priceRange.target ? ' below' : ' above'} target
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recommendations & Risks */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-success-bg rounded-xl border border-green-200 p-4">
              <h4 className="font-semibold text-success flex items-center mb-3">
                <CheckCircle className="h-5 w-5 mr-2" />
                AI Recommendations
              </h4>
              <ul className="space-y-2">
                {analysis.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start text-sm text-success">
                    <ArrowRight className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-fit-moderate-bg rounded-xl border border-yellow-200 p-4">
              <h4 className="font-semibold text-fit-moderate flex items-center mb-3">
                <AlertCircle className="h-5 w-5 mr-2" />
                Risk Factors
              </h4>
              <ul className="space-y-2">
                {analysis.risks.map((risk, i) => (
                  <li key={i} className="flex items-start text-sm text-fit-moderate">
                    <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    {risk}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Comparable Awards */}
          <div className="bg-bw-surface rounded-xl border border-bw-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-bw-ink flex items-center">
                <Award className="h-5 w-5 mr-2 text-bw-primary" />
                Comparable Contract Awards
              </h3>
              <span className="text-sm text-bw-muted">{comparables.length} similar contracts analyzed</span>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-bw-border">
                  <th className="text-left py-2 text-bw-muted font-medium">Contract</th>
                  <th className="text-left py-2 text-bw-muted font-medium">Agency</th>
                  <th className="text-left py-2 text-bw-muted font-medium">Contractor</th>
                  <th className="text-right py-2 text-bw-muted font-medium">Award Value</th>
                  <th className="text-right py-2 text-bw-muted font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {comparables.map((award, i) => (
                  <tr key={i} className="border-b border-bw-border hover:bg-bw-surface-subtle">
                    <td className="py-3">
                      <p className="font-medium text-bw-ink">{award.title}</p>
                      <p className="text-xs text-bw-muted">{award.contractNumber}</p>
                    </td>
                    <td className="py-3 text-bw-muted">{award.agency}</td>
                    <td className="py-3 text-bw-muted">{award.contractor}</td>
                    <td className="py-3 text-right font-medium">{formatCurrency(award.value)}</td>
                    <td className="py-3 text-right text-bw-muted">
                      {new Date(award.awardDate).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4 pt-4 border-t border-bw-border flex items-center justify-between">
              <p className="text-sm text-bw-muted">
                Data sourced from USASpending.gov and FPDS
              </p>
              <a
                href="https://www.usaspending.gov"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-bw-primary hover:text-bw-primary flex items-center"
              >
                View on USASpending.gov
                <ExternalLink className="h-4 w-4 ml-1" />
              </a>
            </div>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-16 bg-bw-surface-subtle rounded-xl border border-blue-200">
          <Target className="h-16 w-16 text-bw-primary mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-bw-ink mb-2">Run Price-to-Win Analysis</h3>
          <p className="text-bw-muted max-w-md mx-auto mb-6">
            AI will analyze historical contract awards, market data, and competitor pricing
            to recommend an optimal price point for this opportunity.
          </p>
          <button
            onClick={runAnalysis}
            disabled={analyzing}
            className="inline-flex items-center px-6 py-3 bg-bw-primary text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50"
          >
            {analyzing ? (
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-5 w-5 mr-2" />
            )}
            Analyze Pricing
          </button>
        </div>
      )}
    </div>
  );
}
